import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, User } from "lucide-react";
import { API_BASE, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SESSION_KEY = "flf_chat_session";
const OPENED_KEY = "flf_chat_opened";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [capture, setCapture] = useState({ name: "", email: "", phone: "" });
  const [captured, setCaptured] = useState(false);
  const scrollRef = useRef(null);

  // Auto-open after 5s once per browser
  useEffect(() => {
    if (localStorage.getItem(OPENED_KEY)) return;
    // hide on /admin routes
    if (window.location.pathname.startsWith("/admin")) return;
    const t = setTimeout(() => {
      setOpen(true);
      localStorage.setItem(OPENED_KEY, "1");
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  // Ensure session + greeting on first open
  useEffect(() => {
    if (!open || sessionId) return;
    (async () => {
      try {
        const { data } = await api.post("/chat/session");
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
        setMessages([{ role: "assistant", text: data.greeting }]);
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || !sessionId || streaming) return;
    const userText = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: userText }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API_BASE}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: userText }),
      });
      if (!resp.ok || !resp.body) throw new Error("Chat failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n").filter(Boolean);
          for (const line of lines) {
            if (line.startsWith("data:")) {
              // Preserve internal whitespace; SSE only reserves a single leading space after "data:"
              let chunk = line.startsWith("data: ") ? line.slice(6) : line.slice(5);
              chunk = chunk.replace(/\\n/g, "\n");
              if (chunk === "end" || chunk === "[error]") continue;
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, text: last.text + chunk };
                return copy;
              });
            }
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.text) copy[copy.length - 1] = { ...last, text: "Sorry, I'm having trouble responding right now. Please try again or call us on 0460 017 777." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const submitCapture = async (e) => {
    e.preventDefault();
    if (!capture.email && !capture.phone) { toast.error("Please add your email or phone"); return; }
    try {
      await api.post("/chat/capture-lead", { session_id: sessionId, ...capture });
      setCaptured(true);
      setShowCapture(false);
      setMessages((m) => [...m, { role: "assistant", text: "Thank you — the team will reach out to you very shortly." }]);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Hide entirely on admin routes
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return null;

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 left-6 z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary-hover transition-all flex items-center justify-center"
        aria-label="Open chat"
        data-testid="chatbot-toggle"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 left-6 z-40 w-[92vw] max-w-sm h-[70vh] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-border-warm flex flex-col overflow-hidden" data-testid="chatbot-panel">
          <div className="bg-secondary text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <MessageCircle size={18} style={{ color: "#C9A961" }} />
            </div>
            <div className="flex-1">
              <p className="font-serif text-lg leading-tight">Family Legacy Films</p>
              <p className="text-xs text-white/60">We're here to help · Sydney & Canberra</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-secondary text-white rounded-br-sm" : "bg-white border border-border-warm text-headline rounded-bl-sm"
                }`}>
                  {m.text || (m.role === "assistant" && streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          {!captured && !showCapture && (
            <div className="px-4 py-2 border-t border-border-warm bg-white">
              <button onClick={() => setShowCapture(true)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="chat-leave-details">
                <User size={12} /> Leave your details for a callback
              </button>
            </div>
          )}

          {showCapture && !captured && (
            <form onSubmit={submitCapture} className="p-4 border-t border-border-warm bg-white space-y-2" data-testid="chat-capture-form">
              <p className="text-xs uppercase tracking-widest text-warmgrey-muted">Leave your details</p>
              <input className="input-field text-sm" placeholder="Name" value={capture.name} onChange={(e) => setCapture({ ...capture, name: e.target.value })} data-testid="capture-name" />
              <input className="input-field text-sm" placeholder="Email" type="email" value={capture.email} onChange={(e) => setCapture({ ...capture, email: e.target.value })} data-testid="capture-email" />
              <input className="input-field text-sm" placeholder="Phone" value={capture.phone} onChange={(e) => setCapture({ ...capture, phone: e.target.value })} data-testid="capture-phone" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowCapture(false)} className="btn-outline text-xs flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-primary text-xs flex-1 justify-center" data-testid="capture-submit">Send</button>
              </div>
            </form>
          )}

          <form onSubmit={send} className="p-3 border-t border-border-warm bg-white flex gap-2">
            <input
              className="input-field text-sm flex-1"
              placeholder={streaming ? "Sending…" : "Type a message…"}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={streaming}
              data-testid="chat-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} className="btn-primary p-3" aria-label="Send" data-testid="chat-send">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
